'use strict';
import isNumber from 'lodash/isNumber';
import memoize from 'lodash/memoize';
import React from 'react';
import * as T from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import {Button} from '../../../components/buttons/Button';
import {I18nComponent} from '../../../core-utilities/react-i18n/I18nComponent';
import {ListItem} from '../../../components/lists/ListItem';
import {calcElapsedTime, ELAPSED_TIME_IDX, TimerDurationStatus} from '../../utils';

const cachedFmtDate = memoize(fmtDate);

export function BaseActiveTimerItemView(props) {
  const elapsedTime = calcElapsedTime(props.elapsedTime);
  const elapsedTimeDays = elapsedTime[ELAPSED_TIME_IDX.DAY];
  const elapsedTimeHours = elapsedTime[ELAPSED_TIME_IDX.HOUR];
  const elapsedTimeMinutes = elapsedTime[ELAPSED_TIME_IDX.MIN];
  return (
    <ListItem
      className={classNames('ActiveTimerItem', props.className, props.scheduleStatus)}
      onClick={props.onClick}
      onContextMenu={props.onContextMenu}
    >
      <div
        className={classNames('ActiveTimerCounter', {
          'ActiveTimerCounter_paused': !props.isStarted,
        })}
        title={renderTimeTitle(elapsedTime)}
      >
        <div className="ActiveTimerCounter__wrapper">
          {elapsedTimeDays > 0 ? renderCounterSection(elapsedTimeDays, 'D') : null}
          {renderCounterSection(elapsedTimeHours, 'H')}
          {elapsedTimeDays > 0 ? null : renderCounterSection(elapsedTimeMinutes, 'M')}
        </div>
      </div>

      <div className="ActiveTimerContent">
        <div className="ActiveTimerContent__title">
          {` ${props.targetName}`}
        </div>
        <div className="ActiveTimerContent__subtitle">
          <i className="icon ion-ios-arrow-back" />
          {` ${props.parentName}`}
        </div>
        <div className="ActiveTimerContent__date">
          {props.isStarted ? (
            `Started: ${cachedFmtDate(props.startedAt)}`
          ) : (
            `Paused: ${cachedFmtDate(props.endedAt)}`
          )}
        </div>
      </div>

      <div className="ActiveTimerItem__button-container">
        <Button
          className="ActiveTimerItem__button ActiveTimerItem__button_stop"
          iconClass="icon ion-stop"
          title={props.stopLabel}
          onClick={props.onClickStop}
        />
        <Button
          className={classNames('ActiveTimerItem__button',
            props.isStarted ? 'ActiveTimerItem__button_pause' : 'ActiveTimerItem__button_resume',
          )}
          iconClass={classNames('icon', props.isStarted ? 'ion-pause' : 'ion-play')}
          title={props.isStarted ? props.pauseLabel : props.resumeLabel}
          onClick={props.isStarted ? props.onClickPause : props.onClickStart}
        />
      </div>
    </ListItem>
  );
}

function renderCounterSection(value, label) {
  return (
    <div className="ActiveTimerCounter__section">
      <div className="ActiveTimerCounter__value">
        {value}
      </div>
      <div className="ActiveTimerCounter__label">
        {label}
      </div>
    </div>
  );
}

function renderTimeTitle(elapsedTime) {
  const secs = `${elapsedTime[ELAPSED_TIME_IDX.SEC]} seconds`;
  const mins = `${elapsedTime[ELAPSED_TIME_IDX.MIN]} minutes`;

  if (elapsedTime[ELAPSED_TIME_IDX.DAY]) {
    return `${elapsedTime[ELAPSED_TIME_IDX.DAY]} days, ${elapsedTime[ELAPSED_TIME_IDX.HOUR]} hours, ${mins}, ${secs}`;
  } else if (elapsedTime[ELAPSED_TIME_IDX.HOUR]) {
    return `${elapsedTime[ELAPSED_TIME_IDX.HOUR]} hours, ${mins}, ${secs}`;
  } else {
    return `${mins}, ${secs}`;
  }
}

BaseActiveTimerItemView.commentIndicatorClassName = '';

BaseActiveTimerItemView.propTypes = {
  className: T.string,

  elapsedTime: T.number,
  startedAt: T.number,
  endedAt: T.number,
  isStarted: T.bool,
  /** @type {TimerDurationStatus} Used to warn user when time elapsed is near or over the specified duration of the task */
  durationStatus: T.instanceOf(TimerDurationStatus),

  targetName: T.string,
  parentName: T.string,

  onClick: T.func,
  onContextMenu: T.func,
  onClickStart: T.func,
  onClickPause: T.func,
  onClickStop: T.func,

  pauseLabel: T.string,
  resumeLabel: T.string,
  stopLabel: T.string,
};

BaseActiveTimerItemView.defaultProps = {
  durationStatus: TimerDurationStatus.NORMAL,
};

function fmtDate(date) {
  if (isNumber(date)) {
    return moment(date).format('LLL');
  } else {
    return null;
  }
}

export const ActiveTimerItemView = I18nComponent(BaseActiveTimerItemView, [
  {code: 'timers.pause.label', prop: 'pauseLabel'},
  {code: 'timers.resume.label', prop: 'resumeLabel'},
  {code: 'timers.stop.label', prop: 'stopLabel'},
]);



// WEBPACK FOOTER //
// ./src/js/timers/components/ActiveTimerItem/ActiveTimerItemView.js